import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Team() {
  const { language } = useLanguage();
  const t = translations[language].team;

  return (
    <section id="team" className="py-20 md:py-32 bg-[#e3e6e8]" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770] mb-4">
            {t.title}
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className="grid md:grid-cols-3 gap-8"
        >
          {t.members.map((member, i) => {
            const isNursingTeam = i === 2;

            return (
              <motion.div
                key={i}
                variants={itemVariants}
                className="group text-center"
                data-testid={`team-member-${i}`}
              >
                <div className="w-24 h-24 mx-auto mb-6">
                  {isNursingTeam ? (
                    <div className="relative w-24 h-24">
                      <div className="absolute top-4 left-1/2 -translate-x-[130%] w-16 h-16 rounded-lg bg-gradient-to-br from-[#fff6e5] to-[#ffeed0] flex items-center justify-center">
                        <User className="w-4 h-4 text-[#ffa600]" />
                      </div>

                      <div className="absolute top-4 left-1/2 translate-x-[30%] w-16 h-16 rounded-lg bg-gradient-to-br from-[#fff6e5] to-[#ffeed0] flex items-center justify-center">
                        <User className="w-4 h-4 text-[#ffa600]" />
                      </div>

                      <div className="absolute top-11 left-1/2 -translate-x-1/2 w-16 h-16 rounded-lg bg-gradient-to-br from-[#fff6e5] to-[#ffeed0] flex items-center justify-center">
                        <User className="w-4 h-4 text-[#ffa600]" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#fff6e5] to-[#ffeed0] flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                      <User className="w-10 h-10 text-[#ffa600]" />
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-semibold text-[#5b6770] mb-1">{member.name}</h3>
                <p className="text-sm font-medium text-[#ffa600] mb-3">{member.role}</p>
                <p className="text-[#5b6770] text-sm leading-relaxed max-w-xs mx-auto">{member.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}